import type { ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * A modal dialog for one task (focus trapped, Escape closes, focus returns to the opening
 * control). Content mounts only while open, so each opening starts fresh.
 */
export function AppDialog(props: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  /**
   * Where focus goes on close, when the opening control is gone by then (a menu item): the
   * control that opened the menu; a function is read on close (the element may be new).
   * `false` leaves focus where it is: another dialog opened in this one's place has it.
   */
  returnFocus?: HTMLElement | null | (() => HTMLElement | null | false);
  /**
   * Where focus starts when it opens; the first focusable control when absent, null or not
   * focusable (disabled).
   */
  initialFocus?: () => HTMLElement | null;
  /** Escape inside the dialog; `preventDefault` keeps it open (a list closing first). */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  children: ReactNode;
}) {
  return (
    <Dialog open={props.open} onOpenChange={(open) => (open ? undefined : props.onClose())}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg"
        onEscapeKeyDown={props.onEscapeKeyDown}
        onOpenAutoFocus={(event) => {
          const target = props.initialFocus?.();
          // A disabled target ignores focus(): keep the default rather than leave focus behind.
          if (target?.isConnected && !target.matches(":disabled")) {
            event.preventDefault();
            target.focus();
          }
        }}
        onCloseAutoFocus={(event) => {
          const target =
            typeof props.returnFocus === "function" ? props.returnFocus() : props.returnFocus;
          if (target === false) {
            event.preventDefault();
            return;
          }
          if (target?.isConnected) {
            event.preventDefault();
            target.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{props.title}</DialogTitle>
          {props.description ? (
            <DialogDescription>{props.description}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">{props.title}</DialogDescription>
          )}
        </DialogHeader>
        {props.open ? props.children : null}
      </DialogContent>
    </Dialog>
  );
}
